/* Once Upon Magic — NEO 2026 progressive enhancements */
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const initHeader = () => {
    const headers = document.querySelectorAll('.header-section');
    if (!headers.length) return;
    const update = () => headers.forEach((header) => header.classList.toggle('scrolled', window.scrollY > 16));
    update();
    window.addEventListener('scroll', update, { passive: true });
  };

  const initReveal = () => {
    const items = document.querySelectorAll('[data-oum-reveal]');
    if (!items.length) return;
    if (reducedMotion || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        obs.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    items.forEach((item) => observer.observe(item));
  };

  const initHeroMotion = () => {
    if (reducedMotion || !window.matchMedia('(hover:hover) and (pointer:fine)').matches) return;
    document.querySelectorAll('[data-oum-neo-hero]').forEach((hero) => {
      const main = hero.querySelector('[data-oum-parallax-main]');
      const small = hero.querySelector('[data-oum-parallax-small]');
      let frame;
      hero.addEventListener('pointermove', (event) => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const rect = hero.getBoundingClientRect();
          const x = (event.clientX - rect.left) / rect.width;
          const y = (event.clientY - rect.top) / rect.height;
          hero.style.setProperty('--oum-pointer-x', `${Math.round(x * 100)}%`);
          hero.style.setProperty('--oum-pointer-y', `${Math.round(y * 100)}%`);
          const dx = x - .5;
          const dy = y - .5;
          if (main) {
            main.style.setProperty('--oum-parallax-x', `${dx * -10}px`);
            main.style.setProperty('--oum-parallax-y', `${dy * -8}px`);
          }
          if (small) {
            small.style.setProperty('--oum-parallax-x', `${dx * 15}px`);
            small.style.setProperty('--oum-parallax-y', `${dy * 12}px`);
          }
        });
      }, { passive: true });
      hero.addEventListener('pointerleave', () => {
        hero.style.removeProperty('--oum-pointer-x');
        hero.style.removeProperty('--oum-pointer-y');
        [main, small].forEach((item) => {
          if (!item) return;
          item.style.removeProperty('--oum-parallax-x');
          item.style.removeProperty('--oum-parallax-y');
        });
      });
    });
  };

  const initMagicDock = () => {
    if (document.querySelector('[data-oum-magic-dock]')) return;
    const main = document.querySelector('main[data-template]');
    if (!main) return;

    const templateName = (main.dataset.template || '').split('.')[0];
    const allowedTemplates = new Set(['index', 'collection', 'search', 'blog']);
    if (!allowedTemplates.has(templateName)) return;

    const rootPath = window.Shopify?.routes?.root || '/';
    const pathname = window.location.pathname;
    const isSearch = templateName === 'search';
    const isNew = pathname.includes('/collections/nouveautes');
    const isHome = templateName === 'index';

    const dock = document.createElement('nav');
    dock.className = 'oum-magic-dock';
    dock.dataset.oumMagicDock = 'true';
    dock.setAttribute('aria-label', 'Navigation rapide');
    dock.innerHTML = `
      <a class="oum-magic-dock__item${isHome ? ' is-active' : ''}" href="${rootPath}"${isHome ? ' aria-current="page"' : ''}>
        <span class="oum-magic-dock__icon" aria-hidden="true">⌂</span><span>Accueil</span>
      </a>
      <a class="oum-magic-dock__item oum-magic-dock__item--magic" href="${rootPath}#oum-magic-swipe">
        <span class="oum-magic-dock__spark" aria-hidden="true">✦</span><span>Magic</span>
      </a>
      <a class="oum-magic-dock__item${isSearch ? ' is-active' : ''}" href="${rootPath}search"${isSearch ? ' aria-current="page"' : ''}>
        <span class="oum-magic-dock__icon" aria-hidden="true">⌕</span><span>Rechercher</span>
      </a>
      <a class="oum-magic-dock__item${isNew ? ' is-active' : ''}" href="${rootPath}collections/nouveautes"${isNew ? ' aria-current="page"' : ''}>
        <span class="oum-magic-dock__icon" aria-hidden="true">◇</span><span>Nouveautés</span>
      </a>`;

    document.body.appendChild(dock);
    document.body.classList.add('oum-has-magic-dock');

    const magicLink = dock.querySelector('.oum-magic-dock__item--magic');
    if (isHome && magicLink) {
      magicLink.addEventListener('click', (event) => {
        const target = document.querySelector('#oum-magic-swipe');
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
        history.replaceState(null, '', '#oum-magic-swipe');
      });
    }

    let lastY = window.scrollY;
    let ticking = false;
    const updateVisibility = () => {
      const currentY = window.scrollY;
      const delta = currentY - lastY;
      if (currentY > 150 && delta > 8) dock.classList.add('is-hidden');
      if (delta < -8 || currentY < 90) dock.classList.remove('is-hidden');
      lastY = currentY;
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(updateVisibility);
    }, { passive: true });
  };

  const boot = () => {
    initHeader();
    initReveal();
    initHeroMotion();
    initMagicDock();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();

  document.addEventListener('shopify:section:load', () => {
    initReveal();
    initHeroMotion();
    initMagicDock();
  });
})();
